/**
 * InstitutionBillingCard — thin billing view for institution admins
 * (role="broker"). Shows:
 *   • Melian Dialogue bank details for annual/monthly subscription
 *   • Trial status + days remaining
 *   • Payment-reference form (broker enters bank slip ref)
 *   • Payment slip upload
 *
 * Closed-loop: NO card-on-file, NO gateway. Everything happens via bank
 * transfer + admin verification.
 */
import { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Building2, Copy, Upload, Check, Landmark } from "lucide-react";

const fmtKES = (n) =>
    typeof n === "number"
        ? `KES ${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`
        : "—";

export default function InstitutionBillingCard() {
    const [bank, setBank]       = useState(null);
    const [agency, setAgency]   = useState(null);
    const [ref, setRef]         = useState("");
    const [busy, setBusy]       = useState(false);
    const [uploading, setUp]    = useState(false);
    const inputRef              = useRef(null);

    async function load() {
        try {
            const [b, a] = await Promise.all([
                api.get("/subscription/bank-details").then((r) => r.data),
                api.get("/agency/me").then((r) => r.data),
            ]);
            setBank(b);
            setAgency(a.agency);
        } catch (e) {
            toast.error("Failed to load billing details");
        }
    }
    useEffect(() => { load(); }, []);

    async function submitRef() {
        if (!ref || ref.length < 4) {
            toast.error("Enter a bank transfer reference (min 4 chars).");
            return;
        }
        setBusy(true);
        try {
            await api.post("/agency/payment-reference", { payment_reference: ref });
            toast.success("Payment reference submitted — admin will verify shortly.");
            setRef("");
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Couldn't submit reference");
        } finally { setBusy(false); }
    }

    async function uploadSlip(e) {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        setUp(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            // Re-use the licence-upload endpoint pattern; a dedicated
            // /agency/upload-payment-slip endpoint can be introduced later
            // — for now the licence storage folder accepts payment slips
            // as an interim compliance artifact.
            await api.post("/agency/upload-license", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success("Payment slip uploaded — admin will verify shortly.");
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Upload failed");
        } finally { setUp(false); }
    }

    if (!bank || !agency) {
        return <div className="text-sm text-muted-foreground p-4">Loading institutional billing…</div>;
    }

    const isMoE = agency.institution_type === "moe";
    const price = bank.prices_kes?.[isMoE ? "moe_institution" : "cma_institution"] || {};
    const trialEnds = agency.trial_ends_at ? new Date(agency.trial_ends_at) : null;
    const daysLeft = trialEnds ? Math.max(0, Math.ceil((trialEnds - Date.now()) / (1000 * 60 * 60 * 24))) : null;

    const copy = (v) => {
        navigator.clipboard.writeText(v).then(
            () => toast.success("Copied"),
            () => toast.error("Copy failed"),
        );
    };

    return (
        <div className="space-y-6" data-testid="institution-billing">
            {/* Trial status */}
            {trialEnds && (
                <div className="border border-primary/40 bg-primary/5 rounded p-4 flex items-start gap-3"
                     data-testid="institution-billing-trial">
                    <Building2 className="w-5 h-5 text-primary shrink-0" />
                    <div className="flex-1">
                        <div className="overline text-primary">Institutional plan · {isMoE ? "MoE" : "CMA"}</div>
                        <p className="text-sm mt-1">
                            {daysLeft > 0
                                ? <>You are on a <strong>free 7-day trial</strong> — <strong>{daysLeft} days</strong> remaining. Pay via bank transfer below before the trial ends to keep your institution active.</>
                                : <>Your trial has ended. Complete a bank transfer to activate your annual plan.</>}
                        </p>
                    </div>
                </div>
            )}

            {/* Bank details */}
            <div className="border border-border bg-card rounded p-5" data-testid="institution-billing-bank">
                <div className="flex items-center gap-2 mb-3">
                    <Landmark className="w-5 h-5 text-primary" />
                    <div className="overline">Melian Dialogue bank details</div>
                </div>
                <div className="grid sm:grid-cols-2 gap-3 text-sm">
                    <Field label="Bank"           value={bank.bank.bank_name}      onCopy={copy} />
                    <Field label="Account name"   value={bank.bank.account_name}   onCopy={copy} />
                    <Field label="Account number" value={bank.bank.account_number} onCopy={copy} mono />
                    <Field label="Branch"         value={bank.bank.branch}         onCopy={copy} />
                    <Field label="SWIFT"          value={bank.bank.swift}          onCopy={copy} mono />
                    <Field label="Reference"      value={agency.agency_name}       onCopy={copy} />
                </div>
                <p className="text-xs text-muted-foreground mt-3">
                    Use your institution name as the payment narration so admins can match your transfer.
                </p>
            </div>

            {/* Pricing */}
            <div className="border border-border bg-card rounded p-5" data-testid="institution-billing-pricing">
                <div className="overline mb-3">Your annual subscription</div>
                <div className="flex items-baseline gap-3">
                    <span className="text-3xl font-extrabold tracking-tight">
                        {fmtKES(price.annual)}<span className="text-sm font-normal text-muted-foreground">/year</span>
                    </span>
                    <span className="text-xs text-muted-foreground">or {fmtKES(price.monthly)}/month</span>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                    Includes: 100 seats, Obondo AI Copilot, unlimited chart history, private leaderboard,
                    dedicated support, and priority activation.
                </p>
            </div>

            {/* Submit payment reference */}
            <div className="border border-border bg-card rounded p-5 space-y-3" data-testid="institution-billing-submit">
                <div className="overline">Submit your bank transfer</div>
                <p className="text-xs text-muted-foreground">
                    After completing the transfer, enter the bank reference and (optionally) upload the slip. Our
                    super-admin verifies within 1 business day and activates your account.
                </p>
                <div className="flex flex-wrap gap-2 items-center">
                    <input
                        type="text"
                        placeholder="Bank transfer reference"
                        value={ref}
                        onChange={(e) => setRef(e.target.value)}
                        className="flex-1 min-w-[220px] h-9 px-3 text-sm border border-border rounded bg-background"
                        data-testid="institution-billing-ref-input"
                    />
                    <button
                        onClick={submitRef}
                        disabled={busy}
                        data-testid="institution-billing-ref-submit"
                        className="h-9 px-4 rounded bg-primary text-primary-foreground text-sm font-semibold disabled:opacity-50"
                    >
                        {busy ? "Submitting…" : "Submit reference"}
                    </button>
                </div>
                <input
                    ref={inputRef}
                    type="file"
                    accept="application/pdf,image/png,image/jpeg,image/webp"
                    className="hidden"
                    onChange={uploadSlip}
                    data-testid="institution-billing-slip-input"
                />
                <button
                    onClick={() => inputRef.current?.click()}
                    disabled={uploading}
                    data-testid="institution-billing-slip-btn"
                    className="inline-flex items-center gap-2 h-9 px-3 rounded border border-border text-sm hover:bg-secondary/40 disabled:opacity-50"
                >
                    <Upload className="w-3.5 h-3.5" />
                    {uploading ? "Uploading…" : "Upload payment slip (PDF / image)"}
                </button>
                {agency.payment_reference && (
                    <div className="text-xs text-success flex items-center gap-1.5 pt-1"
                         data-testid="institution-billing-current-ref">
                        <Check className="w-3.5 h-3.5" /> Reference on file: <span className="mono font-bold">{agency.payment_reference}</span>
                        {agency.payment_verified_at ? " · verified ✓" : " · awaiting verification"}
                    </div>
                )}
            </div>
        </div>
    );
}

function Field({ label, value, onCopy, mono }) {
    return (
        <div className="flex items-center justify-between border border-border/60 rounded px-3 py-2">
            <div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
                <div className={"text-sm " + (mono ? "mono font-bold" : "font-semibold")}>{value || "—"}</div>
            </div>
            {value && (
                <button onClick={() => onCopy(value)} className="text-muted-foreground hover:text-primary"
                        title="Copy" aria-label={`Copy ${label}`}>
                    <Copy className="w-3.5 h-3.5" />
                </button>
            )}
        </div>
    );
}
